import axios, { AxiosResponse } from 'axios';
import {
  ApiResponse,
  LoanSubmissionRequest,
  LoanSubmissionResponse,
  DocumentUploadRequest,
  DocumentUploadResponse,
  DraftSaveRequest,
  DraftSaveResponse
} from '../types/api.types';
import { FormDataType } from '../types/form.types';
import { formatCityForApi, formatNameForApi, formatAddressForApi, formatCompanyNameForApi } from './formatting';
import { sanitizeInsuranceCode } from './insurance';

// Récupérer le clickId Affilae S2S (stocké depuis le paramètre aecid de l'URL de redirection)
function getAffilaeCids(): string[] {
  try {
    const clickId = sessionStorage.getItem('affilae_clickId');
    if (clickId) return [clickId];
  } catch (e) {
    console.warn('Impossible de lire le clickId Affilae:', e);
  }
  return [];
}

// Configuration de l'API
const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || 'http://localhost:3000';
const USE_MOCK_API = (import.meta as any).env?.VITE_USE_MOCK_API === 'true';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  }
});

// Intercepteur pour les requêtes (ajouter des headers)
apiClient.interceptors.request.use(
  (config) => {
    console.log('🚀 Sending request to:', config.url);
    
    if (config.data) {
      if (typeof config.data === 'string') {
        console.log('✅ Data is JSON string, length:', config.data.length);
        
        // Vérifier que c'est un JSON valide
        try {
          JSON.parse(config.data);
          console.log('✅ JSON string validation passed');
        } catch (error) {
          console.error('❌ Invalid JSON string:', error);
          throw new Error('JSON string invalide');
        }
      } else {
        console.log('⚠️ Data is not a string, converting...');
        try {
          const jsonString = JSON.stringify(config.data);
          console.log('✅ Object to JSON conversion successful, length:', jsonString.length);
        } catch (error) {
          console.error('❌ Object to JSON conversion failed:', error);
          throw new Error('Impossible de convertir en JSON');
        }
      }
    }
    
    return config;
  },
  (error) => {
    console.error('❌ Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Intercepteur pour les erreurs
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    console.log('========================================');
    console.log('✅ API RESPONSE SUCCESS');
    console.log('========================================');
    console.log('📍 URL:', response.config.url);
    console.log('🔢 Status:', response.status, response.statusText);
    console.log('📋 Headers:', response.headers);
    console.log('📦 Response Data:');
    console.log(JSON.stringify(response.data, null, 2));
    console.log('========================================');
    return response;
  },
  (error) => {
    console.log('========================================');
    console.error('❌ API RESPONSE ERROR');
    console.log('========================================');
    console.error('📍 URL:', error.config?.url);
    console.error('🔢 Status:', error.response?.status, error.response?.statusText);
    console.error('📋 Response Headers:', error.response?.headers);
    console.error('📦 Response Data:');
    console.error(JSON.stringify(error.response?.data, null, 2));
    console.error('🔧 Request Config:', {
      method: error.config?.method,
      url: error.config?.url,
      headers: error.config?.headers
    });
    console.log('========================================');
    return Promise.reject(error);
  }
);

// Fonction utilitaire pour nettoyer et valider les données
function cleanAndValidateData(formData: FormDataType): FormDataType {
  const cleaned: FormDataType = {};
  
  console.log('🧹 Cleaning form data...');
  
  // Nettoyer les clés et valeurs
  Object.keys(formData).forEach(key => {
    const value = formData[key];
    
    if (value !== undefined && value !== null && value !== '') {
      // Nettoyer les chaînes de caractères
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed.length > 0) {
          cleaned[key] = trimmed;
        }
      } else if (typeof value === 'number') {
        // S'assurer que les nombres sont valides
        if (!isNaN(value) && isFinite(value)) {
          cleaned[key] = value;
        }
      } else if (typeof value === 'boolean') {
        cleaned[key] = value;
      } else {
        // Pour les autres types, vérifier qu'ils ne sont pas des objets vides
        if (value && typeof value === 'object') {
          const stringified = JSON.stringify(value);
          if (stringified !== '{}' && stringified !== '[]') {
            cleaned[key] = value;
          }
        } else {
          cleaned[key] = value;
        }
      }
    }
  });
  
  console.log('✅ Data cleaning completed. Fields:', Object.keys(cleaned));
  return cleaned;
}


// Valider et convertir les nombres
function parseNumber(value: any, defaultValue: number = 0): number {
  if (value === undefined || value === null || value === '') {
    return defaultValue;
  }
  
  const num = typeof value === 'number' ? value : parseFloat(String(value).replace(/[^\d.-]/g, ''));
  
  if (isNaN(num) || !isFinite(num)) {
    return defaultValue;
  }
  
  return Math.round(num);
}

// Formatter la date au format YYYY-MM-DD pour le serveur (finalement c'est ISO qui est requis)
function normalizePhoneNumber(phone: string): string {
  if (!phone) return '';

  let cleaned = String(phone).trim();

  // Gérer les formats internationaux: +33, 0033, etc.
  if (cleaned.startsWith('+33')) {
    // +33612345678 → 0612345678
    cleaned = '0' + cleaned.slice(3);
  } else if (cleaned.startsWith('0033')) {
    // 0033612345678 → 0612345678
    cleaned = '0' + cleaned.slice(4);
  }

  // Enlever tous les espaces et caractères non-numériques sauf le 0 au début
  cleaned = cleaned.replace(/\s/g, '').replace(/[^\d]/g, '');

  // S'assurer qu'il commence par 0
  if (cleaned && !cleaned.startsWith('0') && cleaned.length === 9) {
    cleaned = '0' + cleaned;
  }

  return cleaned;
}

function formatDateForServer(dateValue: any): string {
  if (!dateValue) {
    console.log('⚠️ Empty date value received');
    return '';
  }

  console.log('📅 Formatting date:', dateValue, typeof dateValue);

  try {
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) {
      console.log('❌ Invalid date:', dateValue);
      return '';
    }

    const formatted = date.toISOString().split('T')[0];
    console.log('✅ Date formatted to YYYY-MM-DD:', formatted);
    return formatted;
  } catch (error) {
    console.error('❌ Date formatting error:', error);
    return '';
  }
}


// Labels des professions (Annexe L — Cofidis spec v2.1.2)
const PROFESSION_LABELS: Record<string, string> = {
  '1100': 'Artisans',
  '2100': 'Commerçants',
  '2200': "Chefs d'entreprise",
  '2300': 'Professions agricoles indépendantes',
  '3100': 'Professions libérales',
  '3300': 'Cadres du privé',
  '3800': 'Professions intermédiaires du privé',
  '4500': 'Autres professions du public',
  '4600': 'Autres professions du privé',
  '5200': 'Employés du public',
  '5300': 'Employés du privé',
  '5400': 'Professions agricoles salariées',
  '5500': 'Personnels de maison',
  '5600': 'Apprentis, stagiaires',
  '6300': 'Ouvriers',
  '7700': 'Retraités, pré-retraités',
  '8100': "Demandeurs d'emploi",
  '8400': 'Étudiants',
  '8500': 'Autres inactifs',
};


// Transformer les données du formulaire au format API
function transformFormDataToApiRequest(formData: FormDataType): LoanSubmissionRequest {
  const cleanedData = cleanAndValidateData(formData);
  
  // Validation des champs requis
  const amount = parseNumber(cleanedData.amount);
  const duration = parseNumber(cleanedData.duration);
  
  if (amount < 500 || amount > 35000) {
    throw new Error('Montant invalide: doit être entre 500€ et 35 000€');
  }
  
  if (!duration || duration < 6 || duration > 120) {
    throw new Error('Durée invalide: doit être entre 6 et 120 mois');
  }
  
  if (!cleanedData.firstName || !cleanedData.lastName || !cleanedData.email) {
    throw new Error('Informations personnelles manquantes');
  }
  
  if (!cleanedData.city || !cleanedData.street || !cleanedData.postalCode) {
    throw new Error('Adresse manquante');
  }

  // Construire l'objet de requête selon le format SCHEMA.json
  const requestData: LoanSubmissionRequest = {
    Identification: {
      providerId: 'YELLOAN',
    },
    Simulation: {
      loanAmount: amount,
      duration: duration,
      // demandMaturity: Deadline for making funds available
      // "01": Immediately, "02": Within one month, "03": Within more than one month, "05": Other
      demandMaturity: String(cleanedData.demandMaturity || '01').trim(),
    },
    Borrower: {
      civilStatus: {
        title: String(cleanedData.title || 'M').trim(),
        birthCountryCode: String(cleanedData.birthCountry || 'FR').trim(),
        birthDate: formatDateForServer(cleanedData.birthDate),
        ...(String(cleanedData.birthCountry || 'FR').trim() === 'FR' ? {
          birthCity: formatCityForApi(cleanedData.birthCity || ''),
          birthDepartment: String(cleanedData.birthDepartment || '').trim().slice(0, 3),
        } : {}),
        nationalityCode: 'FR',
        firstName: formatNameForApi(cleanedData.firstName || ''),
        lastName: formatNameForApi(cleanedData.lastName || ''),
        birthName: formatNameForApi(cleanedData.birthName || cleanedData.lastName || '')
      },
      contact: {
        email: String(cleanedData.email || '').trim(),
        optin: cleanedData.acceptYelloanOffers ? 1 : 0,
        optinPartner: cleanedData.acceptPartnerOffers ? 1 : 0,
        optinPhone: false
      },
      incomes: (() => {
        const professionCode = String(cleanedData.professionCode || '4600');
        const codeNum = parseInt(professionCode, 10);
        const incomes: any = {};

        // salaryAmount uniquement pour les actifs (professionCode <= 6300), et seulement si > 0
        if (codeNum <= 6300) {
          const salary = parseNumber(cleanedData.monthlySalary);
          if (salary > 0) incomes.salaryAmount = salary;
        }

        // Autres revenus optionnels
        if (cleanedData.retirementPension && parseNumber(cleanedData.retirementPension) > 0) {
          incomes.retirementPensionAmount = parseNumber(cleanedData.retirementPension);
          incomes.retirementPensionPeriodicityCode = cleanedData.retirementPensionPeriodicity || 'M';
        }
        
        if (cleanedData.disabilityPension && parseNumber(cleanedData.disabilityPension) > 0) {
          incomes.disabilityPensionAmount = parseNumber(cleanedData.disabilityPension);
          incomes.disabilityPensionPeriodicityCode = cleanedData.disabilityPensionPeriodicity || 'M';
        }
        
        if (cleanedData.reversionaryPension && parseNumber(cleanedData.reversionaryPension) > 0) {
          incomes.reversionaryPensionAmount = parseNumber(cleanedData.reversionaryPension);
          incomes.reversionaryPensionPeriodicityCode = cleanedData.reversionaryPensionPeriodicity || 'M';
        }
        
        return Object.keys(incomes).length > 0 ? incomes : undefined;
      })(),
      professionalSituation: (() => {
        const professionCode = String(cleanedData.professionCode || '4600');
        const codeNum = parseInt(professionCode, 10);
        // Spec p.41 : contractTypeCode uniquement si professionCode entre 3300 et 6300 (inclus)
        const needsContractType = codeNum >= 3300 && codeNum <= 6300;
        const isInactiveStatus = codeNum >= 7700;
        const ps: any = { professionCode };
        if (!isInactiveStatus) {
          const seniorityMonths = Math.min(Math.max(parseNumber(cleanedData.seniorityMonths), 0), 720);
          const calculatedStart = new Date(Date.now() - seniorityMonths * 30 * 24 * 60 * 60 * 1000);
          // Filet de sécurité : startDate jamais avant birthDate + 16 ans
          const minStart = cleanedData.birthDate
            ? new Date(new Date(cleanedData.birthDate).setFullYear(new Date(cleanedData.birthDate).getFullYear() + 16))
            : calculatedStart;
          ps.startDate = formatDateForServer(new Date(Math.max(calculatedStart.getTime(), minStart.getTime())));
          ps.profession = PROFESSION_LABELS[professionCode] || professionCode;
        }
        if (needsContractType) {
          ps.contractTypeCode = String(cleanedData.contractTypeCode || 'CDI');
        }
        if (needsContractType && ['CDD', 'INT'].includes(String(cleanedData.contractTypeCode || '')) && cleanedData.contractEndDate) {
          ps.endDate = formatDateForServer(cleanedData.contractEndDate);
        }
        return ps;
      })()
    },
    Address: {
      zipCode: String(cleanedData.postalCode || '').trim(),
      countryCode: 'FR',
      city: formatCityForApi(cleanedData.city || ''),
      street: formatAddressForApi(cleanedData.street || '')
    },
    HousingStatus: {
      statusCode: String(cleanedData.housingStatus || 'L').trim(),
      startDate: formatDateForServer(cleanedData.housingStartDate || new Date()),
      housingExpenses: (() => {
        const housingStatus = cleanedData.housingStatus;
        const expenses: any = {};
        
        if (housingStatus === 'L') {
          // Locataire
          expenses.rentMthAmount = parseNumber(cleanedData.monthlyRent, 0);
        } else if (housingStatus === 'A') {
          // Accédant (avec crédit immobilier)
          expenses.housingLoanMthAmount = parseNumber(cleanedData.monthlyLoanPayment, 0);
          if (cleanedData.housingLoanStartDate) {
            expenses.housingLoanStartDate = formatDateForServer(cleanedData.housingLoanStartDate);
          }
          if (cleanedData.housingLoanEndDate) {
            expenses.housingLoanEndDate = formatDateForServer(cleanedData.housingLoanEndDate);
          }
          if (cleanedData.housingLoanOrganization) {
            expenses.housingLoanOrganizationCode = cleanedData.housingLoanOrganization;
          }
        }
        
        return expenses;
      })()
    },
    FamilyStatus: (() => {
      const familyStatus = String(cleanedData.familyStatus || 'C').trim();
      const familyStatusObj: any = {
        statusCode: familyStatus,
        dependentChildrenNumber: parseNumber(cleanedData.dependentChildren, 0),
        OtherDependantsNumber: 0
      };

      // startDate obligatoire seulement si statusCode ≠ 'C' (Célibataire) — spec p.42
      if (['M', 'P', 'D', 'S', 'V', 'K'].includes(familyStatus)) {
        familyStatusObj.startDate = formatDateForServer(cleanedData.familyStatusStartDate || cleanedData.housingStartDate || new Date());
      }

      return familyStatusObj;
    })(),
    BankAccount: {
      bankCode: String(cleanedData.bankCode || '30004').trim(),
      creationDate: formatDateForServer(cleanedData.accountCreationDate || new Date()),
      ownerCode: String(cleanedData.accountOwnerCode || '01').trim()
    },
    Insurance: (() => {
      // sanitizeInsuranceCode valide le code selon l'âge de l'emprunteur (spec Cofidis : < 65 = tout, 65-75 = DC, ≥ 75 = aucune)
      const borrowerCode = sanitizeInsuranceCode(
        cleanedData.borrowerInsurance ?? null,
        cleanedData.birthDate
      );

      const hasCoBorrower = cleanedData.hasCoBorrower === true;
      const coBorrowerCode = hasCoBorrower
        ? sanitizeInsuranceCode(cleanedData.coBorrowerInsurance ?? null, cleanedData.coBorrowerBirthDate)
        : null;

      const ins: any = {};

      if (borrowerCode !== null) ins.borrowerInsurance = borrowerCode;
      if (hasCoBorrower && coBorrowerCode !== null) ins.coBorrowerInsurance = coBorrowerCode;

      // subscription = true seulement si au moins un code assurance est présent
      ins.subscription = !!(ins.borrowerInsurance || ins.coBorrowerInsurance);

      return ins;
    })()
  };

  // Ajouter le co-emprunteur si l'utilisateur a coché "Ajouter un co-emprunteur"
  // Cofidis rejette le CoBorrower pour 'S' (Séparé) et 'D' (Divorcé) — confirmé par erreur API.
  const familyStatusBlocksCoBorrower = ['S', 'D'].includes(String(cleanedData.familyStatus || ''));
  if (cleanedData.hasCoBorrower === true && !familyStatusBlocksCoBorrower) {
    if (cleanedData.coBorrowerFirstName && cleanedData.coBorrowerLastName && cleanedData.coBorrowerEmail) {
      const coBorrowerData: any = {
        civilStatus: {
          title: String(cleanedData.coBorrowerTitle || 'M').trim(),
          birthCountryCode: String(cleanedData.coBorrowerBirthCountry || 'FR').trim(),
          birthDate: formatDateForServer(cleanedData.coBorrowerBirthDate),
          ...(String(cleanedData.coBorrowerBirthCountry || 'FR').trim() === 'FR' ? {
            birthCity: formatCityForApi(cleanedData.coBorrowerBirthCity || ''),
            birthDepartment: String(cleanedData.coBorrowerBirthDepartment || '').trim().slice(0, 3),
          } : {}),
          nationalityCode: String(cleanedData.coBorrowerNationality || 'FR').trim(),
          firstName: formatNameForApi(cleanedData.coBorrowerFirstName || ''),
          lastName: formatNameForApi(cleanedData.coBorrowerLastName || ''),
          birthName: formatNameForApi(cleanedData.coBorrowerBirthName || cleanedData.coBorrowerLastName || '')
        },
        contact: {
          email: String(cleanedData.coBorrowerEmail || '').trim(),
          optin: cleanedData.acceptYelloanOffers ? 1 : 0,
          optinPartner: cleanedData.acceptPartnerOffers ? 1 : 0,
          optinPhone: false
        },
        incomes: (() => {
          const professionCode = String(cleanedData.coBorrowerProfessionCode || '4600');
          const codeNum = parseInt(professionCode, 10);
          const incomes: any = {};

          // salaryAmount uniquement pour les actifs (professionCode <= 6300), et seulement si > 0
          if (codeNum <= 6300) {
            const salary = parseNumber(cleanedData.coBorrowerMonthlySalary);
            if (salary > 0) incomes.salaryAmount = salary;
          }

          if (cleanedData.coBorrowerRetirementPension && parseNumber(cleanedData.coBorrowerRetirementPension) > 0) {
            incomes.retirementPensionAmount = parseNumber(cleanedData.coBorrowerRetirementPension);
            incomes.retirementPensionPeriodicityCode = cleanedData.coBorrowerRetirementPensionPeriodicity || 'M';
          }

          if (cleanedData.coBorrowerDisabilityPension && parseNumber(cleanedData.coBorrowerDisabilityPension) > 0) {
            incomes.disabilityPensionAmount = parseNumber(cleanedData.coBorrowerDisabilityPension);
            incomes.disabilityPensionPeriodicityCode = cleanedData.coBorrowerDisabilityPensionPeriodicity || 'M';
          }

          if (cleanedData.coBorrowerReversionaryPension && parseNumber(cleanedData.coBorrowerReversionaryPension) > 0) {
            incomes.reversionaryPensionAmount = parseNumber(cleanedData.coBorrowerReversionaryPension);
            incomes.reversionaryPensionPeriodicityCode = cleanedData.coBorrowerReversionaryPensionPeriodicity || 'M';
          }

          return incomes;
        })(),
        professionalSituation: (() => {
          const professionCode = String(cleanedData.coBorrowerProfessionCode || '4600');
          const codeNum = parseInt(professionCode, 10);
          // Spec p.41 : contractTypeCode uniquement si professionCode entre 3300 et 6300 (inclus)
          const needsContractType = codeNum >= 3300 && codeNum <= 6300;
          const isInactiveStatus = codeNum >= 7700;
          const ps: any = { professionCode };
          if (!isInactiveStatus) {
            const coBorrowerSeniorityMonths = Math.min(Math.max(parseNumber(cleanedData.coBorrowerSeniorityMonths), 0), 720);
            const calculatedStart = new Date(Date.now() - coBorrowerSeniorityMonths * 30 * 24 * 60 * 60 * 1000);
            const minStart = cleanedData.coBorrowerBirthDate
              ? new Date(new Date(cleanedData.coBorrowerBirthDate).setFullYear(new Date(cleanedData.coBorrowerBirthDate).getFullYear() + 16))
              : calculatedStart;
            ps.startDate = formatDateForServer(new Date(Math.max(calculatedStart.getTime(), minStart.getTime())));
            ps.profession = PROFESSION_LABELS[professionCode] || professionCode;
          }
          if (needsContractType) {
            ps.contractTypeCode = String(cleanedData.coBorrowerContractTypeCode || 'CDI');
          }
          if (needsContractType && ['CDD', 'INT'].includes(String(cleanedData.coBorrowerContractTypeCode || '')) && cleanedData.coBorrowerContractEndDate) {
            ps.endDate = formatDateForServer(cleanedData.coBorrowerContractEndDate);
          }
          return ps;
        })()
      };

      // Ajouter le téléphone du co-emprunteur si présent
      if (cleanedData.coBorrowerPhone && String(cleanedData.coBorrowerPhone).trim()) {
        const cleanPhone = normalizePhoneNumber(String(cleanedData.coBorrowerPhone));
        if (/^0[67]\d{8}$/.test(cleanPhone)) {
          coBorrowerData.contact.mobilePhone = cleanPhone;
        }
      }

      // Spec p.30 : employer uniquement si professionCode entre 3300 et 6300
      const coBorrowerProfessionCodeNum = parseInt(String(cleanedData.coBorrowerProfessionCode || '4600'), 10);
      const coBorrowerCanHaveEmployer = coBorrowerProfessionCodeNum >= 3300 && coBorrowerProfessionCodeNum <= 6300;

      if (coBorrowerCanHaveEmployer && cleanedData.coBorrowerCompanyName && String(cleanedData.coBorrowerCompanyName).trim()) {
        const cityCodes = [3300, 3800, 6300];
        coBorrowerData.professionalSituation.employer = {
          name: formatCompanyNameForApi(String(cleanedData.coBorrowerCompanyName)),
          countryCode: 'FR',
          ...(cityCodes.includes(coBorrowerProfessionCodeNum) && cleanedData.coBorrowerCompanyCity
            ? { city: formatCityForApi(String(cleanedData.coBorrowerCompanyCity)) }
            : {})
        };
      }

      requestData.CoBorrower = coBorrowerData;
    }
  }

  // Ajouter les champs optionnels
  if (cleanedData.phone && String(cleanedData.phone).trim()) {
    const cleanPhone = normalizePhoneNumber(String(cleanedData.phone));
    if (/^0[67]\d{8}$/.test(cleanPhone)) {
      requestData.Borrower.contact.mobilePhone = cleanPhone;
    }
  }
  
  // Spec p.30 : employer doit uniquement être renseigné si professionCode entre 3300 et 6300
  const borrowerProfessionCodeNum = parseInt(String(cleanedData.professionCode || '4600'), 10);
  const borrowerCanHaveEmployer = borrowerProfessionCodeNum >= 3300 && borrowerProfessionCodeNum <= 6300;

  if (borrowerCanHaveEmployer && cleanedData.companyName && String(cleanedData.companyName).trim()) {
    const cityCodes = [3300, 3800, 6300];
    const employer: any = {
      name: formatCompanyNameForApi(String(cleanedData.companyName)),
      countryCode: 'FR',
      ...(cityCodes.includes(borrowerProfessionCodeNum) && cleanedData.companyCity
        ? { city: formatCityForApi(String(cleanedData.companyCity)) }
        : {})
    };

    // Ajouter code postal si présent
    if (cleanedData.companyPostalCode && String(cleanedData.companyPostalCode).trim()) {
      const postalCode = String(cleanedData.companyPostalCode).trim();
      if (/^[0-9]{5}$/.test(postalCode)) {
        employer.postalCode = postalCode;
      }
    }

    // Ajouter code région si présent
    if (cleanedData.companyRegionCode && String(cleanedData.companyRegionCode).trim()) {
      employer.regionCode = String(cleanedData.companyRegionCode).trim();
    }

    requestData.Borrower.professionalSituation.employer = employer;
  }

  // Ajouter FamilyIncomes si au moins un revenu est présent
  const dependentChildren = parseNumber(cleanedData.dependentChildren, 0);
  // Cofidis rejette alimonyMthAmount dans FamilyIncomes si dependentChildrenNumber = 0
  const alimonyReceived = dependentChildren > 0 ? parseNumber(cleanedData.alimonyReceivedMthAmount, 0) : 0;
  // Spec p.24 : housingAllowanceMthAmount must not be filled if housingStatus = 'P' or 'V'
  const housingStatusCode = String(cleanedData.housingStatus || 'L').trim();
  const housingAllowance = !['P', 'V'].includes(housingStatusCode) ? parseNumber(cleanedData.housingAllowanceMthAmount, 0) : 0;
  // Cofidis rejette familyAllowanceMthAmount si dependentChildren = 0
  const familyAllowance = dependentChildren > 0 ? parseNumber(cleanedData.otherIncome, 0) : 0;

  if (alimonyReceived > 0 || housingAllowance > 0 || familyAllowance > 0) {
    const familyIncomes: any = {};
    // Spec : alimonyMthAmount must not be filled if dependentChildrenNumber = 0 ; min value = 1
    if (dependentChildren > 0 && alimonyReceived >= 1) {
      familyIncomes.alimonyMthAmount = alimonyReceived;
    }
    // Spec : familyAllowanceMthAmount must not be filled if dependentChildrenNumber = 0 ; min value = 1
    if (dependentChildren > 0 && familyAllowance >= 1) {
      familyIncomes.familyAllowanceMthAmount = familyAllowance;
    }
    // Spec : housingAllowanceMthAmount must not be filled if housingStatus = 'P' or 'V' (already filtered above)
    if (housingAllowance > 0) {
      familyIncomes.housingAllowanceMthAmount = housingAllowance;
    }
    if (Object.keys(familyIncomes).length > 0) {
      requestData.FamilyIncomes = familyIncomes;
    }
  }

  // Ajouter FamilyExpenses si au moins une charge est présente
  const alimonyPaid = parseNumber(cleanedData.alimonyPaidMthAmount, 0);
  const externalCredits: any[] = Array.isArray(cleanedData.externalCredits) ? cleanedData.externalCredits : [];

  if (alimonyPaid > 0 || externalCredits.length > 0) {
    requestData.FamilyExpenses = {
      // Spec : alimonyMthAmount must not be filled if dependentChildrenNumber = 0 ; min value = 1
      ...(dependentChildren > 0 && alimonyPaid >= 1 ? { alimonyMthAmount: alimonyPaid } : {}),
      // Spec : otherCreditsMthAmount only if externalCredits >= 10
      ...(externalCredits.length >= 10 && cleanedData.otherCreditsMthAmount > 0
        ? { otherCreditsMthAmount: parseNumber(cleanedData.otherCreditsMthAmount) }
        : {}),
    };

    if (externalCredits.length > 0) {
      requestData.FamilyExpenses.externalCredits = externalCredits
        .filter(c => c.type && c.organization && c.startDate && c.monthlyAmount > 0)
        .map(c => {
          const entry: any = {
            organizationCode: String(c.organization).trim(),
            productType: c.type,
            startDate: formatDateForServer(c.startDate),
            monthlyAmount: c.monthlyAmount,
          };
          // Spec : endDate must not be filled if productType is 'PREREN'
          if (c.type !== 'PREREN' && c.endDate) {
            entry.endDate = formatDateForServer(c.endDate);
          }
          return entry;
        });
    }
  }

  return requestData;
}

// Fonction pour vérifier si un objet peut être sérialisé en JSON
function validateJsonSerializable(obj: any): void {
  console.log('🔍 Validating JSON serialization...');
  
  try {
    // Première vérification : est-ce que l'objet peut être sérialisé ?
    const serialized = JSON.stringify(obj);
    
    if (!serialized || serialized.length === 0) {
      throw new Error('Objet vide après sérialisation');
    }
    
    if (serialized === 'undefined' || serialized === 'null') {
      throw new Error('Objet undefined ou null');
    }
    
    // Deuxième vérification : est-ce que le JSON peut être parsé ?
    const parsed = JSON.parse(serialized);
    
    // Vérifier les valeurs problématiques récursivement
    function checkValues(o: any, path = ''): void {
      if (o === null || o === undefined) {
        console.log(`⚠️ Null/undefined value at ${path}`);
        return;
      }
      
      if (typeof o === 'number' && (isNaN(o) || !isFinite(o))) {
        throw new Error(`Valeur numérique invalide à ${path}: ${o}`);
      }
      
      if (typeof o === 'string' && o.includes('\u0000')) {
        throw new Error(`Caractère null dans la chaîne à ${path}`);
      }
      
      if (typeof o === 'function') {
        throw new Error(`Fonction trouvée à ${path} (non sérialisable)`);
      }
      
      if (typeof o === 'object' && o !== null) {
        if (o instanceof Date) {
          // Les dates sont ok
          return;
        }
        
        if (o instanceof RegExp) {
          throw new Error(`RegExp trouvée à ${path} (non sérialisable)`);
        }
        
        Object.keys(o).forEach(key => {
          checkValues(o[key], path ? `${path}.${key}` : key);
        });
      }
    }
    
    checkValues(parsed);
    console.log('✅ JSON serialization validation passed');
    
  } catch (error) {
    console.error('❌ JSON serialization validation failed:', error);
    throw new Error(`Données non sérialisables: ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
  }
}

// Mock API pour tester sans backend
async function mockApiCall(): Promise<LoanSubmissionResponse> {
  // Simuler un délai de réseau
  await new Promise(resolve => setTimeout(resolve, 2000));
  
  // Simuler une réponse de succès
  return {
    IdDemand: 'LOAN_' + Date.now(),
    DemandStatus: 'pending',
    Token: 'TOKEN_' + Date.now(),
    DigitalSignatureEligibility: true,
    BorrowerUrl: 'https://example.com/sign/borrower',
    BorrowerSingleUseUrl: 'https://example.com/sign/borrower/single',
    CoBorrowerUrl: null,
    BrokerUrl: null,
    RepaymentMonthlyAmount: '250',
    TotalAmountDue: 15000,
    Taeg: '5.5',
    NominalRate: '5.0',
    ClientProfile: 'STANDARD',
    CounterProposal: null,
    RepaymentDegrRate: 0,
    InsuranceTotalCost: '500',
    InsuranceRepaymentAmount: '25',
    RepaymentMonthlyAmountWithoutInsurance: 225,
    Reason: null,
    CCPCreditNumber: 0,
    CCPMonthlyLoanAmount: 0,
    ok: true,
    monthlyInsurance: 25,
    TotalAmountWithoutInsurance: '14500'
  };
}

// Soumettre la demande de crédit
export async function submitCreditApplication(formData: FormDataType): Promise<ApiResponse<LoanSubmissionResponse>> {
  try {
    console.log('🔄 Starting API submission...');
    console.log('Raw form data:', formData);
    
    // Vérifier que les données essentielles sont présentes
    if (!formData.amount || !formData.duration || !formData.firstName || !formData.lastName || !formData.email) {
      throw new Error('Données manquantes dans le formulaire');
    }
    
    const requestData = transformFormDataToApiRequest(formData);
    console.log('✅ Data transformation completed');
    
    // Validation approfondie de la sérialisation JSON
    validateJsonSerializable(requestData);
    console.log('✅ JSON serialization validation passed');
    
    // Double vérification du JSON
    let jsonString: string;
    try {
      jsonString = JSON.stringify(requestData);
      console.log('✅ JSON stringification successful, length:', jsonString.length);
    } catch (stringifyError) {
      console.error('❌ JSON stringify failed:', stringifyError);
      throw new Error('Impossible de sérialiser les données en JSON');
    }
    
    // Test de parsing du JSON
    try {
      const parsed = JSON.parse(jsonString);
      console.log('✅ JSON parsing validation passed');
      console.log('📋 Parsed object keys:', Object.keys(parsed));
    } catch (parseError) {
      console.error('❌ JSON parsing failed:', parseError);
      console.error('🔍 Problematic JSON:', jsonString.substring(0, 200));
      throw new Error('JSON malformé détecté');
    }
    
    let responseData: LoanSubmissionResponse;
    
    if (USE_MOCK_API) {
      console.log('🧪 Using mock API');
      responseData = await mockApiCall();
    } else {
      console.log('🌐 Sending to real API...');
      const iframeParams = new URLSearchParams(window.location.search);
      // affiliateTrackingId : on utilise le aecid Affilae pour identifier l'affilié dans Airtable
      const affilaeCids = getAffilaeCids();
      const affiliateTrackingId = affilaeCids[0] || iframeParams.get('affiliateTrackingId') || iframeParams.get('ae') || sessionStorage.getItem('affiliateTrackingId');
      const endpointParams = new URLSearchParams({ skippreprocessing: 'true' });
      if (affiliateTrackingId) {
        endpointParams.set('affiliateTrackingId', affiliateTrackingId);
      }
      if (affilaeCids.length > 0) {
        endpointParams.set('affilaeCids', affilaeCids.join(','));
      }
      const endpoint = `/demand-request-result?${endpointParams.toString()}`;
      console.log('🎯 API endpoint:', endpoint);
      console.log('📤 Final payload:', JSON.stringify(requestData, null, 2));
      
      // IMPORTANT: Convertir en JSON string avec validation stricte
      let jsonPayload: string;
      try {
        // Première conversion
        jsonPayload = JSON.stringify(requestData, null, 0); // Pas d'indentation
        
        // Vérification qu'il n'y a pas de caractères cachés
        const cleanPayload = jsonPayload.replace(/[\u0000-\u001F\u007F-\u009F]/g, '');
        if (cleanPayload !== jsonPayload) {
          console.warn('⚠️ Caractères de contrôle détectés et supprimés');
          jsonPayload = cleanPayload;
        }
        
        // Validation finale
        JSON.parse(jsonPayload);
        console.log('✅ JSON payload validation successful');
        
      } catch (error) {
        console.error('❌ JSON payload creation failed:', error);
        throw new Error('Impossible de créer un JSON valide');
      }
      
      console.log('📦 Sending JSON string, length:', jsonPayload.length);
      
      const response = await apiClient.post(endpoint, jsonPayload, {
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
          'Accept': 'application/json'
        }
      });
      responseData = response.data;
      console.log('✅ API response received');
      console.log('🎯 LOAN SUBMISSION RESPONSE:', JSON.stringify(responseData, null, 2));

      // Vérifier si le backend retourne une erreur
      if ((responseData as any).ok === false || (responseData as any).error) {
        const errorData = (responseData as any).error;
        let errorMessage = 'Erreur de validation du formulaire';

        // Formater les erreurs Zod si présentes
        if (errorData?.issues && Array.isArray(errorData.issues)) {
          const issues = errorData.issues.map((issue: any) => {
            const field = issue.path.join('.');
            return `${field}: ${issue.message}`;
          }).join(', ');
          errorMessage = `Erreur de validation: ${issues}`;
        } else if (errorData?.message) {
          errorMessage = errorData.message;
        }

        throw new Error(errorMessage);
      }
    }

    return {
      success: true,
      data: responseData
    };
  } catch (error: any) {
    console.error('API submission error:', error);
    console.error('Error response:', error.response?.data);
    
    let errorMessage = 'Erreur lors de la soumission';
    
    if (error.code === 'ERR_NETWORK') {
      errorMessage = 'Erreur de connexion: Impossible de joindre le serveur. Vérifiez que le backend est démarré sur le port 3000.';
    } else if (error.message && error.message.includes('sérialisables')) {
      errorMessage = `Erreur de format des données: ${error.message}`;
    } else if (error.response?.status === 404) {
      errorMessage = 'Endpoint non trouvé. Vérifiez que l\'endpoint /demand-request-result existe sur le serveur.';
    } else if (error.response?.status >= 500) {
      errorMessage = 'Erreur serveur. Vérifiez les logs du backend.';
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    return {
      success: false,
      error: errorMessage
    };
  }
}

// Sauvegarder un brouillon
export async function saveDraftApplication(draftData: DraftSaveRequest): Promise<ApiResponse<DraftSaveResponse>> {
  try {
    console.log('💾 Saving draft application...');
    const response = await apiClient.post('/save-draft', draftData);
    console.log('🎯 DRAFT SAVE RESPONSE:', JSON.stringify(response.data, null, 2));

    return {
      success: true,
      data: response.data
    };
  } catch (error: any) {
    console.error('❌ Draft save failed:', error.response?.data || error.message);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Erreur lors de la sauvegarde'
    };
  }
}

// Uploader un document
export async function uploadDocument(uploadRequest: DocumentUploadRequest): Promise<ApiResponse<DocumentUploadResponse>> {
  try {
    console.log('📤 Uploading document:', uploadRequest.type);
    const formData = new FormData();
    formData.append('file', uploadRequest.file);
    formData.append('type', uploadRequest.type);
    if (uploadRequest.description) {
      formData.append('description', uploadRequest.description);
    }

    const response = await apiClient.post('/upload-document', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 60000 // 60 secondes pour les uploads
    });
    console.log('🎯 DOCUMENT UPLOAD RESPONSE:', JSON.stringify(response.data, null, 2));

    return {
      success: true,
      data: response.data
    };
  } catch (error: any) {
    console.error('❌ Document upload failed:', error.response?.data || error.message);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Erreur lors de l\'upload'
    };
  }
}

// Vérifier le statut de santé de l'API
export async function checkApiHealth(): Promise<ApiResponse> {
  try {
    console.log('🏥 Checking API health...');
    const response = await apiClient.get('/healthcheck');
    console.log('🎯 HEALTH CHECK RESPONSE:', JSON.stringify(response.data, null, 2));

    return {
      success: true,
      data: response.data
    };
  } catch (error: any) {
    console.error('❌ Health check failed:', error.response?.data || error.message);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'API indisponible'
    };
  }
}

// Hook personnalisé pour les appels API avec retry
export async function apiCallWithRetry<T>(
  apiCall: () => Promise<ApiResponse<T>>,
  maxRetries: number = 3,
  delay: number = 1000
): Promise<ApiResponse<T>> {
  let lastError: any;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const result = await apiCall();
      if (result.success) {
        return result;
      }
      lastError = result.error;
    } catch (error) {
      lastError = error;
    }
    
    if (attempt < maxRetries) {
      // Attendre avant le prochain essai (backoff exponentiel)
      await new Promise(resolve => setTimeout(resolve, delay * Math.pow(2, attempt - 1)));
    }
  }
  
  return {
    success: false,
    error: lastError || 'Toutes les tentatives ont échoué'
  };
}

// Utilitaire pour formater les erreurs API
export function formatApiError(error: any): string {
  if (typeof error === 'string') {
    return error;
  }
  
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }
  
  if (error?.message) {
    return error.message;
  }
  
  return 'Une erreur inattendue s\'est produite';
}